/*
  Состояние вкладки «Файлы»: присланные файлы, сводка минимума, последний перенос.

  Всё, что знает про сервер, - здесь; компоненты вкладки получают готовое
  пропсами (правило 1 скилла frontend).

  Пока хоть один файл читается исполнителем, список переспрашивается сам: отчёт
  1С на десять мегабайт читается десятки секунд, и человек не должен обновлять
  страницу, чтобы увидеть, что файл разобран.

  Так же - пока идёт перенос в портфель (01.10.2026): он стал фоновой работой,
  на тысячах счетов идёт минуты, и итог вкладка узнаёт, спросив снова. Открыли
  страницу посреди переноса - первый же ответ говорит «идёт», и опрос
  начинается сам.

  Сбой опроса его не останавливает (01.10.2026, проверка перед выкладкой).
  Раньше ошибка оставляла прежние данные, эффект с таймером больше не
  перезапускался, и один ответ 502 навсегда замораживал «перенос идёт».
  Теперь неудавшийся запрос повторяется через `RETRY_PAUSE` - и во время
  опроса, и после действия, - а ошибка остаётся в `error`, чтобы вкладка
  показала её поверх имеющихся данных.

  🔴 Сводка минимума - отдельным запросом (02.10.2026, жалоба владельца
  «вкладка подтормаживает»). Она собирает все сведения накопителя: на стенде
  6 224 счёта, около 0,7 с. Список файлов приходит сам и сразу, плитки -
  следом. Опрос гоняет только список, а сводку перечитывает, когда что-то
  изменилось по существу (`revisionOf`): иначе тяжёлый расчёт шёл бы каждые
  три секунды, пока исполнитель читает файл. После действия человека
  перечитывается и то, и другое.

  🔴 С 21.10.2026 (требование владельца: пагинация серверная) список файлов сюда НЕ
  приходит. Опрос спрашивает у сервера только то, что нужно вкладке про ВСЕ файлы, и
  просит для этого окно в одну строку (`limit=1`): последний перенос, `reading` - читается
  ли хоть один файл, `revision` - отпечаток набора и состояний файлов. Строки таблицы,
  карточки сведений организации и сканы шага домовой книги читают свои хуки по тому же
  адресу, но с собственным окном и отбором; перечитываются они, когда меняется `stamp`
  (отпечаток плюс счётчик действий человека).
*/

import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../../api/client.js'

export const POLL_PAUSE = 3000

// Вдвое реже обычного опроса: сервер, только что ответивший отказом, частыми
// повторами не торопят.
export const RETRY_PAUSE = POLL_PAUSE * 2

/*
  Когда сведения накопителя могли измениться сами: файл добавлен, прочитан или
  удалён, кончился перенос. Строкой, а не объектом: новый объект на каждый опрос
  перечитывал бы зависимые списки раз в три секунды без причины. Отпечаток файлов
  считает сервер по всем файлам, а не по той странице, что показана.
*/
export const revisionOf = (data) => (data ? `${data.revision}|${data.last_transfer?.state ?? ''}` : '')

export default function useUploads() {
  const [state, setState] = useState({ data: null, error: null })
  const [summary, setSummary] = useState({ value: null, error: null })
  // Сколько раз человек что-то сделал: часть сведений строки (число счетов в файле,
  // метка «хранится, пока жив счёт») меняется без смены состояния файла.
  const [version, setVersion] = useState(0)

  const reloadList = useCallback(
    () =>
      api
        .uploads({ limit: 1 })
        .then((data) => setState({ data, error: null }))
        .catch((error) => setState((prev) => ({ ...prev, error }))),
    [],
  )

  const reloadSummary = useCallback(
    () =>
      api
        .uploadsSummary()
        .then((value) => setSummary({ value, error: null }))
        .catch((error) => setSummary((prev) => ({ ...prev, error }))),
    [],
  )

  // После действия человека - и список, и сводку. Ждём только список: по нему
  // кнопка снимает «идёт», а плитки догонят сами.
  const reload = useCallback(() => {
    setVersion((value) => value + 1)
    reloadSummary()
    return reloadList()
  }, [reloadList, reloadSummary])

  useEffect(() => {
    reloadList()
  }, [reloadList])

  /*
    Сводка спрашивается при открытии - вместе со списком, а не после него, - и
    затем при каждой смене `revisionOf`. Первый ответ списка поводом не
    считается: сводка к этому времени уже спрошена.
  */
  const revision = revisionOf(state.data)
  const seen = useRef(null)
  useEffect(() => {
    const firstAnswer = seen.current === ''
    seen.current = revision
    if (!firstAnswer) reloadSummary()
  }, [revision, reloadSummary])

  const reading = Boolean(state.data?.reading)
  const transferring = state.data?.last_transfer?.state === 'running'
  // Зависимость - весь `state`, а не `state.data`: при ошибке данные прежние, и
  // по ним эффект не перезапустился бы (01.10.2026).
  useEffect(() => {
    if (!state.error && !reading && !transferring) return undefined
    const timer = setTimeout(reloadList, state.error ? RETRY_PAUSE : POLL_PAUSE)
    return () => clearTimeout(timer)
  }, [reading, transferring, state, reloadList])

  // Сбой сводки повторяется так же, как сбой списка: вкладка обещает человеку,
  // что Арбитр ЖКХ повторит запрос сам.
  useEffect(() => {
    if (!summary.error) return undefined
    const timer = setTimeout(reloadSummary, RETRY_PAUSE)
    return () => clearTimeout(timer)
  }, [summary, reloadSummary])

  return {
    data: state.data,
    // Что менять строкам списков и карточкам: отпечаток файлов и счётчик действий.
    stamp: `${revision}|${version}`,
    error: state.error ?? summary.error,
    summary: summary.value,
    summaryError: summary.error,
    reading,
    transferring,
    reload,
  }
}
